import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { AuthContext } from '../shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 🆕 صندوق الموافقات الموحَّد — Pending Approvals Inbox
 * مكان واحد يجمع كل الطلبات المعلّقة (تخفيضات استثنائية + تغييرات خدمة الرحلة)
 * للمدير العام أو المحاسب العام.
 */
const fmt = (n) => Number(n || 0).toLocaleString();

const StatusBadge = ({ status }) => {
  if (status === 'pending_approval') {
    return <Badge className="bg-amber-100 text-amber-900 border border-amber-300">⏳ معلّق</Badge>;
  }
  if (status === 'approved') {
    return <Badge className="bg-emerald-100 text-emerald-900 border border-emerald-300">✅ مُعتمد</Badge>;
  }
  if (status === 'rejected') {
    return <Badge className="bg-red-100 text-red-900 border border-red-300">❌ مرفوض</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
};

const PendingApprovalsInbox = () => {
  const { user } = useContext(AuthContext);
  const isApprover = user?.role === 'super_admin' || user?.role === 'general_accountant';

  const [loading, setLoading] = useState(false);
  const [creditNotes, setCreditNotes] = useState([]);
  const [serviceChanges, setServiceChanges] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  // 🆕 Approvers default to "pending" (their work queue). Non-approvers
  //    default to "all" so they can see the full lifecycle of what they
  //    submitted (pending → approved or rejected).
  const [statusFilter, setStatusFilter] = useState(isApprover ? 'pending_approval' : 'all');
  const [rejectDialog, setRejectDialog] = useState(null); // {type, item} | null
  const [rejectReason, setRejectReason] = useState('');
  const [actionInFlight, setActionInFlight] = useState(null);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  }), []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // 🆕 Backend already scopes by created_by/requested_by for non-admin
      //    callers, so the same endpoints power both "inbox" and "my
      //    requests" views.
      const scQuery = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const rfQuery = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const [crRes, scRes, rfRes] = await Promise.all([
        axios.get(`${API}/credit-notes`, { headers: authHeaders() }),
        axios.get(`${API}/trip-service-change-requests${scQuery}`, { headers: authHeaders() }),
        axios.get(`${API}/refund-requests${rfQuery}`, { headers: authHeaders() }),
      ]);
      const allCRs = (crRes.data?.items || crRes.data || []);
      setCreditNotes(
        statusFilter === 'all' ? allCRs : allCRs.filter((c) => c.status === statusFilter)
      );
      setServiceChanges(scRes.data?.items || []);
      setRefundRequests(rfRes.data?.items || []);
    } catch (e) {
      console.error('[pending-approvals] fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const approveCreditNote = async (cr) => {
    // 🆕 Inline confirm via row-level "armed" state would be ideal, but
    // keeping it lightweight: use a single status feedback path. The
    // native window.confirm is unreliable inside the preview iframe.
    setActionInFlight(`cr-${cr.id}`);
    try {
      await axios.post(`${API}/credit-notes/${cr.id}/approve`, {}, { headers: authHeaders() });
      alert(`✅ تم اعتماد ${cr.credit_note_number} بقيمة ${fmt(cr.amount)} دج`);
      await fetchAll();
    } catch (e) {
      alert('فشل الاعتماد: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionInFlight(null);
    }
  };

  const approveServiceChange = async (req) => {
    setActionInFlight(`sc-${req.id}`);
    try {
      const r = await axios.post(`${API}/trip-service-change-requests/${req.id}/approve`, {}, { headers: authHeaders() });
      alert(
        `✅ تم اعتماد تغيير الخدمة\n` +
        `الرحلة: ${req.trip_no || ''}\n` +
        `الخدمة الجديدة: ${req.new_service_name}\n` +
        `فرق السعر: ${r.data?.price_difference > 0 ? '+' : ''}${fmt(r.data?.price_difference)} دج`
      );
      await fetchAll();
    } catch (e) {
      alert('فشل الاعتماد: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionInFlight(null);
    }
  };

  const approveRefundRequest = async (req) => {
    setActionInFlight(`rf-${req.id}`);
    try {
      const r = await axios.post(`${API}/refund-requests/${req.id}/approve`, {}, { headers: authHeaders() });
      alert(
        `✅ تم اعتماد طلب الاسترجاع\n` +
        `${req.kind === 'ticket' ? 'تذكرة' : 'عملية'}: ${req.source_no || '—'}\n` +
        `قيمة الاسترجاع: ${fmt(req.amount)} دج\n` +
        `رقم وصل الاسترجاع: ${r.data?.refund_operation_no || '—'}`
      );
      await fetchAll();
    } catch (e) {
      alert('فشل الاعتماد: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionInFlight(null);
    }
  };

  const submitReject = async () => {
    if (!rejectDialog) return;
    if (!rejectReason.trim()) {
      alert('سبب الرفض إلزامي');
      return;
    }
    const { type, item } = rejectDialog;
    setActionInFlight(`reject-${item.id}`);
    try {
      if (type === 'credit_note') {
        await axios.post(`${API}/credit-notes/${item.id}/reject`, { reason: rejectReason.trim() }, { headers: authHeaders() });
      } else if (type === 'refund_request') {
        await axios.post(`${API}/refund-requests/${item.id}/reject`, { reason: rejectReason.trim() }, { headers: authHeaders() });
      } else {
        const fd = new FormData();
        fd.append('rejection_reason', rejectReason.trim());
        await axios.post(`${API}/trip-service-change-requests/${item.id}/reject`, fd, { headers: authHeaders() });
      }
      setRejectDialog(null);
      setRejectReason('');
      await fetchAll();
    } catch (e) {
      alert('فشل الرفض: ' + (e.response?.data?.detail || e.message));
    } finally {
      setActionInFlight(null);
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-600">
          🔒 يلزم تسجيل الدخول
        </CardContent>
      </Card>
    );
  }

  const totalPending = creditNotes.length + serviceChanges.length + refundRequests.length;
  const pageTitle = isApprover ? '📥 صندوق الموافقات' : '📋 طلباتي';
  const pageSubtitle = isApprover
    ? (statusFilter === 'pending_approval'
        ? `${totalPending} طلب يحتاج قرارك`
        : `${totalPending} سجل`)
    : `${totalPending} طلب قمت بتقديمه`;
  // Show approve/reject buttons only for approvers AND only on pending rows.
  const showActions = isApprover && statusFilter === 'pending_approval';

  return (
    <div className="space-y-4" dir="rtl" data-testid="pending-approvals-page">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <span>{pageTitle}</span>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded px-2 py-1"
                data-testid="approvals-status-filter"
              >
                {!isApprover && <option value="all">📋 كل طلباتي</option>}
                <option value="pending_approval">⏳ المعلّقة{isApprover ? ' فقط' : ''}</option>
                <option value="approved">✅ المعتمدة</option>
                <option value="rejected">❌ المرفوضة</option>
              </select>
              <Button onClick={fetchAll} size="sm" variant="outline">🔄 تحديث</Button>
            </div>
          </CardTitle>
          <p className="text-xs text-gray-600">{pageSubtitle}</p>
        </CardHeader>
        <CardContent>
          {loading && <div className="text-center text-gray-500 py-6">جاري التحميل…</div>}
          {!loading && totalPending === 0 && (
            <div className="text-center text-gray-500 py-10 text-sm">
              ✨ {isApprover ? 'لا توجد طلبات بهذه الحالة حالياً' : 'لا توجد طلبات بهذه الحالة'}
            </div>
          )}

          {!loading && totalPending > 0 && (
            <Tabs defaultValue="all">
              <TabsList className="grid grid-cols-4 mb-3">
                <TabsTrigger value="all" data-testid="approvals-tab-all">
                  الكل ({totalPending})
                </TabsTrigger>
                <TabsTrigger value="credit_notes" data-testid="approvals-tab-cr">
                  💸 تخفيضات ({creditNotes.length})
                </TabsTrigger>
                <TabsTrigger value="service_changes" data-testid="approvals-tab-sc">
                  🔄 تغيير خدمة ({serviceChanges.length})
                </TabsTrigger>
                <TabsTrigger value="refund_requests" data-testid="approvals-tab-rf">
                  ↩️ استرجاعات ({refundRequests.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-2">
                {[...creditNotes.map((c) => ({ kind: 'credit_note', item: c })),
                  ...serviceChanges.map((s) => ({ kind: 'service_change', item: s })),
                  ...refundRequests.map((r) => ({ kind: 'refund_request', item: r }))]
                  .sort((a, b) => {
                    const dA = a.item.created_at || a.item.requested_at || '';
                    const dB = b.item.created_at || b.item.requested_at || '';
                    return dA.localeCompare(dB);
                  })
                  .map(({ kind, item }) => (
                    kind === 'credit_note' ? (
                      <CreditNoteRow key={`cr-${item.id}`} cr={item} showActions={showActions && item.status === 'pending_approval'}
                        onApprove={() => approveCreditNote(item)}
                        onReject={() => setRejectDialog({ type: 'credit_note', item })}
                        loading={actionInFlight === `cr-${item.id}` || actionInFlight === `reject-${item.id}`} />
                    ) : kind === 'service_change' ? (
                      <ServiceChangeRow key={`sc-${item.id}`} req={item} showActions={showActions && item.status === 'pending_approval'}
                        onApprove={() => approveServiceChange(item)}
                        onReject={() => setRejectDialog({ type: 'service_change', item })}
                        loading={actionInFlight === `sc-${item.id}` || actionInFlight === `reject-${item.id}`} />
                    ) : (
                      <RefundRequestRow key={`rf-${item.id}`} req={item} showActions={showActions && item.status === 'pending_approval'}
                        onApprove={() => approveRefundRequest(item)}
                        onReject={() => setRejectDialog({ type: 'refund_request', item })}
                        loading={actionInFlight === `rf-${item.id}` || actionInFlight === `reject-${item.id}`} />
                    )
                  ))}
              </TabsContent>

              <TabsContent value="credit_notes" className="space-y-2">
                {creditNotes.map((cr) => (
                  <CreditNoteRow key={cr.id} cr={cr} showActions={showActions && cr.status === 'pending_approval'}
                    onApprove={() => approveCreditNote(cr)}
                    onReject={() => setRejectDialog({ type: 'credit_note', item: cr })}
                    loading={actionInFlight === `cr-${cr.id}` || actionInFlight === `reject-${cr.id}`} />
                ))}
                {creditNotes.length === 0 && <div className="text-center text-gray-500 py-6 text-sm">لا توجد سندات</div>}
              </TabsContent>

              <TabsContent value="service_changes" className="space-y-2">
                {serviceChanges.map((req) => (
                  <ServiceChangeRow key={req.id} req={req} showActions={showActions && req.status === 'pending_approval'}
                    onApprove={() => approveServiceChange(req)}
                    onReject={() => setRejectDialog({ type: 'service_change', item: req })}
                    loading={actionInFlight === `sc-${req.id}` || actionInFlight === `reject-${req.id}`} />
                ))}
                {serviceChanges.length === 0 && <div className="text-center text-gray-500 py-6 text-sm">لا توجد طلبات</div>}
              </TabsContent>

              <TabsContent value="refund_requests" className="space-y-2">
                {refundRequests.map((req) => (
                  <RefundRequestRow key={req.id} req={req} showActions={showActions && req.status === 'pending_approval'}
                    onApprove={() => approveRefundRequest(req)}
                    onReject={() => setRejectDialog({ type: 'refund_request', item: req })}
                    loading={actionInFlight === `rf-${req.id}` || actionInFlight === `reject-${req.id}`} />
                ))}
                {refundRequests.length === 0 && <div className="text-center text-gray-500 py-6 text-sm">لا توجد طلبات استرجاع</div>}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={(o) => { if (!o) { setRejectDialog(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>❌ رفض الطلب</DialogTitle>
            <DialogDescription className="text-xs text-gray-600">
              يرجى توضيح سبب الرفض ليظهر للطالب في سجل الطلبات.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              {rejectDialog?.type === 'credit_note'
                ? `سند التخفيض ${rejectDialog?.item?.credit_note_number} (${fmt(rejectDialog?.item?.amount)} دج)`
                : rejectDialog?.type === 'refund_request'
                  ? `طلب استرجاع ${rejectDialog?.item?.kind === 'ticket' ? 'تذكرة' : 'عملية'} ${rejectDialog?.item?.source_no || ''} (${fmt(rejectDialog?.item?.amount)} دج)`
                  : `طلب تغيير خدمة الرحلة ${rejectDialog?.item?.trip_no || ''}`}
            </p>
            <div>
              <Label className="text-sm">سبب الرفض *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="اشرح للطالب سبب الرفض"
                rows={3}
                data-testid="approvals-reject-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectDialog(null); setRejectReason(''); }}>إلغاء</Button>
            <Button
              onClick={submitReject}
              disabled={!rejectReason.trim() || !!actionInFlight}
              className="bg-red-600 hover:bg-red-700"
              data-testid="approvals-reject-submit"
            >
              {actionInFlight ? '⏳' : '❌ تأكيد الرفض'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Row components ---------------------------------------------------------
const CreditNoteRow = ({ cr, showActions, onApprove, onReject, loading }) => (
  <div className="border rounded-md p-3 bg-amber-50/40 border-amber-200 flex flex-col md:flex-row md:items-start gap-3" data-testid={`cr-row-${cr.id}`}>
    <div className="flex-1 min-w-0 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-amber-900">💸 {cr.credit_note_number}</span>
        <StatusBadge status={cr.status} />
        <span className="text-[11px] text-gray-500">
          بواسطة {cr.created_by_name || '—'} ({cr.created_by_role || '—'})
        </span>
      </div>
      <div className="text-gray-800 mt-1">
        {cr.pilgrim_or_client_name || '—'} • {cr.booking_label || '—'}
      </div>
      <div className="mt-1 text-xs text-gray-700">
        المبلغ: <span className="font-semibold">{fmt(cr.amount)} دج</span> •{' '}
        المصدر: {cr.deduction_source === 'agency_balance' ? '🏢 رصيد الوكالة' : '🏦 الخزينة المركزية'}
      </div>
      {cr.reason && <div className="mt-1 text-xs text-gray-600">📝 السبب: {cr.reason}</div>}
      {cr.notes && <div className="mt-1 text-[11px] text-gray-500">📌 {cr.notes}</div>}
      {cr.document_image_url && (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api${cr.document_image_url}`}
          target="_blank" rel="noreferrer"
          className="mt-1 inline-block text-[11px] text-blue-700 hover:underline"
        >📎 عرض الوثيقة</a>
      )}
      {cr.status === 'rejected' && cr.rejection_reason && (
        <div className="mt-1 text-[11px] text-red-700">❌ سبب الرفض: {cr.rejection_reason}</div>
      )}
    </div>
    {showActions && (
      <div className="flex flex-col gap-1 md:w-32 shrink-0">
        <Button size="sm" disabled={loading} onClick={onApprove}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          data-testid={`cr-approve-${cr.id}`}>
          {loading ? '⏳' : '✅ اعتماد'}
        </Button>
        <Button size="sm" variant="outline" disabled={loading} onClick={onReject}
          className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
          data-testid={`cr-reject-${cr.id}`}>
          ❌ رفض
        </Button>
      </div>
    )}
  </div>
);

const ServiceChangeRow = ({ req, showActions, onApprove, onReject, loading }) => (
  <div className="border rounded-md p-3 bg-orange-50/40 border-orange-200 flex flex-col md:flex-row md:items-start gap-3" data-testid={`sc-row-${req.id}`}>
    <div className="flex-1 min-w-0 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-orange-900">🔄 {req.trip_no || req.id?.slice(0, 8)}</span>
        <StatusBadge status={req.status} />
        <span className="text-[11px] text-gray-500">
          بواسطة {req.requested_by_name || '—'} ({req.requested_by_role || '—'})
        </span>
      </div>
      <div className="text-gray-800 mt-1">
        من: <span className="text-gray-600">{req.old_service_name || '—'}</span> ←{' '}
        إلى: <span className="font-semibold">{req.new_service_name || '—'}</span>
      </div>
      <div className="mt-1 text-xs text-gray-700">
        السعر الجديد: <span className="font-semibold">{fmt(req.new_base_price)} دج</span> •{' '}
        المصدر: {req.deduction_source === 'agency_balance' ? '🏢 رصيد الوكالة' : '🏦 الخزينة المركزية'}
      </div>
      {req.reason && <div className="mt-1 text-xs text-gray-600">📝 السبب: {req.reason}</div>}
      {req.document_image_url && (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api${req.document_image_url}`}
          target="_blank" rel="noreferrer"
          className="mt-1 inline-block text-[11px] text-blue-700 hover:underline"
        >📎 عرض الوثيقة</a>
      )}
      {req.status === 'rejected' && req.rejection_reason && (
        <div className="mt-1 text-[11px] text-red-700">❌ سبب الرفض: {req.rejection_reason}</div>
      )}
    </div>
    {showActions && (
      <div className="flex flex-col gap-1 md:w-32 shrink-0">
        <Button size="sm" disabled={loading} onClick={onApprove}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          data-testid={`sc-approve-${req.id}`}>
          {loading ? '⏳' : '✅ اعتماد'}
        </Button>
        <Button size="sm" variant="outline" disabled={loading} onClick={onReject}
          className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
          data-testid={`sc-reject-${req.id}`}>
          ❌ رفض
        </Button>
      </div>
    )}
  </div>
);

const RefundRequestRow = ({ req, showActions, onApprove, onReject, loading }) => (
  <div className="border rounded-md p-3 bg-rose-50/40 border-rose-200 flex flex-col md:flex-row md:items-start gap-3" data-testid={`rf-row-${req.id}`}>
    <div className="flex-1 min-w-0 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-rose-900">
          ↩️ استرجاع {req.kind === 'ticket' ? 'تذكرة' : 'عملية'} {req.source_no || '—'}
        </span>
        <StatusBadge status={req.status} />
        <span className="text-[11px] text-gray-500">
          بواسطة {req.requested_by_name || '—'} ({req.requested_by_role || '—'})
        </span>
      </div>
      <div className="text-gray-800 mt-1">
        {req.client_name || '—'}{req.service_name ? ` • ${req.service_name}` : ''}
      </div>
      <div className="mt-1 text-xs text-gray-700">
        قيمة الاسترجاع: <span className="font-semibold">{fmt(req.amount)} دج</span>
      </div>
      {req.reason && <div className="mt-1 text-xs text-gray-600">📝 السبب: {req.reason}</div>}
      {req.image_url && (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api${req.image_url}`}
          target="_blank" rel="noreferrer"
          className="mt-1 inline-block text-[11px] text-blue-700 hover:underline"
        >📎 عرض الوثيقة</a>
      )}
      {req.status === 'rejected' && req.rejection_reason && (
        <div className="mt-1 text-[11px] text-red-700">❌ سبب الرفض: {req.rejection_reason}</div>
      )}
      {req.status === 'approved' && req.refund_operation_no && (
        <div className="mt-1 text-[11px] text-emerald-700">✅ رقم وصل الاسترجاع: {req.refund_operation_no}</div>
      )}
    </div>
    {showActions && (
      <div className="flex flex-col gap-1 md:w-32 shrink-0">
        <Button size="sm" disabled={loading} onClick={onApprove}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
          data-testid={`rf-approve-${req.id}`}>
          {loading ? '⏳' : '✅ اعتماد'}
        </Button>
        <Button size="sm" variant="outline" disabled={loading} onClick={onReject}
          className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
          data-testid={`rf-reject-${req.id}`}>
          ❌ رفض
        </Button>
      </div>
    )}
  </div>
);

export default PendingApprovalsInbox;
